
import { IManagementPhase, IRegroupingContractLine } from '@/entity/management/management-phase';
import { IProject } from '@/entity/project/project';
import { IBootstrapTableColumn } from '@/entity/shared/bootstrap';
import { managementApi } from '@/wapi/management-api';
import { ICancellableResult, isCallValidAndNotCancelled } from '@t/ajax-wrapper';
import { NU } from '@t/type';
import { format } from 'date-fns';
import frenchLocale from 'date-fns/locale/fr';
import { Component, Vue, Watch } from 'vue-property-decorator';
import PercentageInput from '@c/shared/percentage-input.vue';
import { vxm } from '@/store';
import { BTable } from 'bootstrap-vue';
import { IPlanificationPhase } from '@/entity/planification/planification-phase';
import ProjectManagementModal from './project-management-modal.vue';
import { getEmployeCost } from '@t/cost-utils';
import { formatCurrencyByProject } from '@t/format-currency'

@Component({
    components: {
        PercentageInput,
        ProjectManagementModal
    }
})
export default class ProjectManagements extends Vue {
    managementPhases: NU<IManagementPhase[]> = [];
    regroupingContractLineList: IRegroupingContractLine[] = [];
    targetRegroupingContractLineId: number = 0;
    rafp: any[] = [];
    advancementKey: number = 0;

    savePending: boolean = false;
    isBusy: boolean = false;
    private promiseExport: boolean = false;
    selectedPlanificationPhaseToUpdate: IPlanificationPhase = {} as IPlanificationPhase;

    redirectToImputationRecap() {
        window.open(`${window.location.origin}/users/imputations-recap#project-id=${this.selectedProjectId}`, '_blank')?.focus();
    }

    get filteredManagementPhases(): IManagementPhase[] {
        const result =
            this.managementPhases?.filter(
                (x) =>
                    this.regroupingContractLineList.find((y) => y.attachedContractLineId === x.contractLine.id) ==
                        null ||
                    this.regroupingContractLineList.find(
                        (y) =>
                            y.targetContractLineId === this.targetRegroupingContractLineId &&
                            x.contractLine.id === y.attachedContractLineId
                    ) != null
            ) ?? [];

        var resultWithAvcmtRange = result.map(function (item) {
            if (item.contractLine.advancement > 100) {
                item.contractLine.advancement = 100;
            }
            if (item.contractLine.advancement < 0) {
                item.contractLine.advancement = 0;
            }
            return item;
        });
        return resultWithAvcmtRange;
    }

    get selectedProject(): NU<IProject> {
        return vxm.project.dropdownProject;
    }

    get selectedProjectId(): NU<number> {
        return vxm.project.dropdownProject?.id;
    }

    get totalRate(): string {
    let val = 0;
    this.managementPhases?.forEach((element) => {
        val += element.contractLine.total;
        });
        return this.formatAmountWithCurrency(val);
    }    

    get totalRegroupingRate(): string {
        let val = 0;
        this.managementPhases?.forEach((element) => {
            val += element.regroupingTotal;
        });
        val = Math.round(val);
        return this.formatAmountWithCurrency(
            val
        );
    }

    get totalProvisionalFees(): string {
        let val = 0;
        this.managementPhases?.forEach((element) => {
            val += element.provisionalFeesValue;
        });
       return this.formatAmountWithCurrency(
            val
        );
    }

    get totalSubContracts(): string {
        let val = 0;
        this.managementPhases?.forEach((element) => {
            val += element.subContractsValue;
        });
        return this.formatAmountWithCurrency(
            val
        );
    }

    get totalMargePercentage(): string {
        let val = 0;
        let totalHonors = 0;
        let totalBudget = 0;
        this.managementPhases?.forEach((element) => {
            totalHonors += element.contractLine.total - element.provisionalFeesValue - element.subContractsValue;
            totalBudget += this.getBudget(element);
        });
        val = 100 - (totalBudget / totalHonors) * 100;
         return `${new Intl.NumberFormat('fr-FR', { style: 'percent', maximumFractionDigits: 0 }).format(
            (isNaN(val) ? 0 : val) / 100
        )}`;
    }

    get totalMargeAmount(): string {
        let totalHonors = 0;
        let totalBudget = 0;
        this.managementPhases?.forEach((element) => {
            totalHonors += element.regroupingTotal - element.provisionalFeesValue - element.subContractsValue;
            totalBudget += this.getBudget(element);
        });
          return `${this.formatAmountWithCurrency(totalHonors - totalBudget)}`;
    }

    get totalBudget(): string {
        return this.formatAmountWithCurrency(
            this.totalBudgetValue
        );
    }

    get totalBudgetValue(): number {
        let val = 0;
        this.managementPhases?.forEach((element) => {
            val += this.getBudget(element);
        });
        return val;
    }

     get totaConsumedRates(): string {
        return this.formatAmountWithCurrency(
            this.totaConsumedRatesValue
        );
    }

    get totaConsumedRatesValue(): number {
        let val = 0;
        this.managementPhases?.forEach((element) => {
            val += element.consumedRatesValue;
        });
        return val;
    }

    get totalRafp() {
        return formatCurrencyByProject(this.totalRafpValue)
    }

    setAttachedContractLinesPercentage(item: IManagementPhase) {
        if (this.isTarget(item)) {
            const attachedRegroupedIdList = this.regroupingContractLineList
                .filter(element => element.targetContractLineId === item.contractLine.id)
                .map(element => element.attachedContractLineId);          
            const attachedPhaseList = this.managementPhases?.filter(phase => attachedRegroupedIdList.includes(phase.contractLine.id));
            attachedPhaseList?.forEach(phase => {
                phase.contractLine.marginPercentage = item.contractLine.marginPercentage;
                phase.contractLine.advancement = item.contractLine.advancement;
            });
        }
    }

    setAttachedContractLinesPercentageById(targetId: number) {
        const targetPhase = this.getTargetPhaseById(targetId);
        if (targetPhase) {
            this.setAttachedContractLinesPercentage(targetPhase);
        }
    }

    onBlurPercentage(item: IManagementPhase) {
        if (this.isTarget(item)) {
            this.submitRegrouping(item);
            this.setAttachedContractLinesPercentage(item);
        }
    }

    getRafpContracLine(item: IManagementPhase): number {
        return this.rafp.find(_ => _.id === item.contractLine.planificationInContractL?.id)?.total ?? 0;
    }

    getTotalGroupedValue(item: IManagementPhase, field : string = ''): number {
        const targetPhaseIdList = this.regroupingContractLineList.map(element => element.targetContractLineId);

        if (targetPhaseIdList.includes(item.contractLine.id) && !this.isAriaSelected(item)) {
            const attachedRegroupedIdList = this.regroupingContractLineList
                .filter(element => element.targetContractLineId === item.contractLine.id)
                .map(element => element.attachedContractLineId);

            const attachedPhaseList = this.managementPhases?.filter(phase => attachedRegroupedIdList.includes(phase.contractLine.id));

            const targetPhase = this.managementPhases?.find(phase => phase.contractLine.id === item.contractLine.id);

            if (field === 'getBudget') {
                let val = targetPhase ? this.getBudget(targetPhase) : 0;
                
                attachedPhaseList?.forEach(phase => {
                    val += this.getBudget(phase);
                });
                return Number(val);
            }

            if (field === 'rafp') {
                let val = targetPhase ? this.getRafpContracLine(targetPhase) : 0;
                
                attachedPhaseList?.forEach(phase => {
                    val += this.getRafpContracLine(phase);
                });
                return Number(val);
            }

            if (field === 'progressAmount') {
                let val = targetPhase ? this.progressAmount(targetPhase) : 0;
                
                attachedPhaseList?.forEach(phase => {
                    val += this.progressAmount(phase);
                });
                return Number(val);
            }
            if (field === 'terminationSituation') {
                let val = targetPhase ? this.getTerminationSituation(targetPhase) : 0;
                
                attachedPhaseList?.forEach(phase => {
                    val += this.getTerminationSituation(phase);
                });
                return Number(val);
            }
           
            let val = targetPhase?.[field] ?? 0;

            attachedPhaseList?.forEach(phase => {
                val += phase[field];
            });

            return val;
        }
        else {
            if (field === 'getBudget') {
                return this.getBudget(item);
            }
            if (field === 'rafp') {
                return this.getRafpContracLine(item);
            }
            if (field === 'progressAmount') {
                return this.progressAmount(item);
            }
            if (field === 'terminationSituation') {
                return this.getTerminationSituation(item);
            }
            return item[`${field}`];
        }
    }


    get totalCostRates(): string {
        let val = 0;
        this.managementPhases?.forEach((element) => {
            val += element.planificationPhaseCostValue;
        });
        return this.formatAmountWithCurrency(
            val
        );
    }

    get totalProgressAmountsValue(): number {
        let val = 0;
        this.managementPhases?.forEach((element) => {
            val += this.progressAmount(element);
        });
        return val;
    }

    get totalProgressAmounts(): string {
        return this.formatAmountWithCurrency(
            this.totalProgressAmountsValue
        );
    }

    getTerminationSituation(item: IManagementPhase) : number {
        return this.getBudget(item) - item.consumedRatesValue - this.getRafpContracLine(item);
    }

    get totalTerminationSituationValue(): number {
        const val : number = this.totalBudgetValue - this.totalRafpValue - this.totaConsumedRatesValue;
        return val;
    }

    get totalTerminationSituation(): string {
        return this.formatAmountWithCurrency(
            this.totalTerminationSituationValue
        );
    }

    private fields: Array<IBootstrapTableColumn> = [
        { label: '', key: 's', sortable: false, thStyle: 'max-width: 100px;', tdClass: 'td-s-width' },
        {
            label: 'Contrat',
            key: 'contractReference',
            formatter: (val: string, key?: string, item?) => val.toUpperCase(),
            tdClass: 'td-class bold-class'
        },
        {
            label: 'Mission',
            key: 'contractLine.designation',
            formatter: (val: string, key?: string, item?) => val?.toUpperCase(),
            tdClass: 'td-class bold-class',
            thStyle: 'min-width:230px'
        },
        {
            label: 'Date de Début',
            key: 'contractLine.planificationInContractL.openingDate',
            formatter: (val: Date, key?: string, item?) => (val ? new Date(val).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'),            
            tdClass: 'td-class'
        },
        {
            label: 'Date de fin',
            key: 'contractLine.planificationInContractL.closingDate',
            formatter: (val: Date, key?: string, item?) => (val ? new Date(val).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'),
            tdClass: 'td-class'
        },
        {
            label: 'Honoraires',
            key: 'contractLine.total',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    val ?? 0
                ),
            tdClass: 'td-class'
        },
        {
            label: 'Honos regroupés',
            key: 'regroupingTotal',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    val ?? 0
                ),
            tdClass: 'td-class'
        },
        {
            label: 'Frais',
            key: 'provisionalFeesValue',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    val ?? 0
                ),
            tdClass: 'td-class',
            tooltip: 'Frais prévisionnels'
        },
        {
            label: 'STT',
            key: 'subContractsValue',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    val ?? 0
                ),
            tdClass: 'td-class',
            tooltip: 'Bons de commande émis + Bons de commande prévisionnels non émis'
        },
        {
            label: 'Marge',
            key: 'marginPercentage',
            thStyle: 'min-width:95px;width:95px',
            tdClass: 'td-class'
        },
        {
            label: 'Budget',
            key: 'getBudget',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    this.getBudget(item) ?? 0
                ),
            tdClass: 'td-class',
            tooltip: 'Honoraires regroupées - Frais - STT - Marge calculée'
        },
        {
            label: 'Prévisionnel',
            key: 'planificationPhaseCostValue',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    val ?? 0
                ),
            tdClass: 'td-class',
            tooltip: 'Planification'
        },
        {
            label: 'Reste à faire prévisionnel (RAFP)',
            key: 'rafp',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    val ?? 0
                ),
            tdClass: 'td-class',
            tooltip: 'Prévisionnel de la planification projet à partir de la date du jour (date du jour incluse)'
        },
        {
            label: 'Consommé',
            key: 'consumedRatesValue',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    val ?? 0
                ),
            tdClass: 'td-class',
            tooltip: 'Imputation sur la phase'
        },
        {
            label: 'Avcmt',
            key: 'advancement',
            thStyle: 'min-width:95px;width:95px',
            tdClass: 'td-class',
            tooltip: 'Pourcentage d\'avancement de la mission'
        },
        {
            label: 'Situ selon avcmt',
            key: 'progressAmount',
            formatter: (val: number, key?: string, item?) =>
                this.formatAmountWithCurrency(
                    val ?? 0
                ),
            tdClass: 'td-class',
            thStyle: 'min-width:100px',
            tooltip: 'Budget restant par rapport à l\'Avancement'
        },
        {
            label: 'Situation à terminaison',
            key: 'terminationSituation',
            thStyle: 'min-width:95px;width:95px',
            tdClass: 'td-class',
            tooltip: 'Budget - (Consommé + RAFP)'
        },
        {
            label: '',
            key: 'regrouping',
            thStyle: 'min-width:100px;width:100px'
        }
    ];

    get formatedOpeningDate(): string {
        if (this.selectedProject?.openingDate) {
            return (
                format(new Date(String(this.selectedProject?.openingDate)), 'EEEE dd MMMM yyyy', {
                    locale: frenchLocale
                }) ?? ''
            );
        }
        return '';
    }

    get formatedClosingDate(): string {
        if (this.selectedProject?.closingDate) {
            return (
                format(new Date(String(this.selectedProject?.closingDate)), 'EEEE dd MMMM yyyy', {
                    locale: frenchLocale
                }) ?? ''
            );
        }
        return '';
    }

    get isMargeReadOnly(): boolean {
        return (vxm.user.isAssociate || vxm.user.isDaf || vxm.user.isStudioManager || vxm.user.isCP) && !vxm.user.isAdmin && !vxm.user.isRH; 
    }

    isTarget(item: IManagementPhase) : boolean {
        const targetPhaseIdList = this.regroupingContractLineList.map(element => element.targetContractLineId);

        return targetPhaseIdList.includes(item.contractLine.id);
    }

    getTargetPhaseById(targetId: number) : IManagementPhase | undefined {
        this.managementPhases?.find(phase => phase.contractLine.id == targetId)
        return this.managementPhases?.find(phase => phase.contractLine.id == targetId);
    }

    isRegrouped(item: IManagementPhase) : boolean {
        const regroupedContracLine = this.regroupingContractLineList.find(regroupedContracLine => 
            regroupedContracLine.attachedContractLineId === item.contractLine.id || regroupedContracLine.targetContractLineId === item.contractLine.id);
        return regroupedContracLine !== undefined;
    }

    isAriaSelected(item: IManagementPhase): boolean {
        const element = document.getElementById(`__BVID__1543__row_${item.contractLine.id}`);
        
        const ariaSelected = element?.getAttribute('aria-selected');

        return ariaSelected !== undefined && ariaSelected === 'true'; 
    }

    get isAvcmtReadOnly(): boolean {
        return (vxm.user.isAssociate || vxm.user.isDaf) && !vxm.user.isAdmin && !vxm.user.isRH && !vxm.user.isCP && !vxm.user.isStudioManager;
    }

    @Watch('selectedProjectId')
    async assigneProject(newVal: number, _oldVal: number): Promise<void> {
        if (newVal && newVal > 0) {
            await this.fetchData(newVal);
        }
    }

    @Watch('managementPhases')
    reloadData() {
        this.getPlanificationPhases();
    }

    log(item: any) {
        return 0;
    }

    @Watch('regroupingContractLineList')
    assigneregrouping(newVal: IRegroupingContractLine[], _oldVal: IRegroupingContractLine[]): void {
        this.setSelectedRowForGroupingLine(newVal);
    }

    async getPlanificationPhases(): Promise<void> {
        this.rafp = [];
        const projectEntityLabel = this.selectedProject?.entity?.label;
        this.managementPhases?.forEach((_, index) => {
            this.rafp.push({ id: _.contractLine.planificationInContractL?.id, total: 0 });
            _.contractLine.planificationInContractL?.planificationPhaseProfiles.forEach((__) => {
                this.rafp[index].total +=
                        __.planificationPhaseProfileWorkDays.filter((x) => new Date(x.date).setHours(0, 0, 0, 0) >= new Date(Date.now()).setHours(0, 0, 0, 0))
                            .length * getEmployeCost(__.employeeRole!,projectEntityLabel!);
            });
        });
    }

    formatAmountWithCurrency(value:number) : string {
        return formatCurrencyByProject(value, { minimumFractionDigits: 0, maximumFractionDigits: 0});
    }

    get totalRafpValue() : number {
        return this.rafp?.map(_ => _.total).reduce((a, b) => a + b, 0);
    }

    private setSelectedRowForGroupingLine(val: IRegroupingContractLine[]): void {
        const table = this.$refs['management-table'] as BTable;
        this.filteredManagementPhases?.forEach((element, index) => {
            const regroupingContractLine = val.find((x) => x.attachedContractLineId === element.contractLine.id);
            if (
                (regroupingContractLine || element.contractLine.id === this.targetRegroupingContractLineId) &&
                this.targetRegroupingContractLineId !== 0
            ) {
                setTimeout(() => {
                    table.selectRow(index);
                }, 100);
            } else {
                if (table) {
                    setTimeout(() => {
                        table.unselectRow(index);
                    }, 100);
                }
            }
        });
    }

    async getManagementPhases(projectId: number): Promise<void> {
        this.isBusy = true;
        const phaseCallData = await managementApi.getAllPhasesByProjectId(projectId);

        if (isCallValidAndNotCancelled<IManagementPhase[]>(phaseCallData)) {
            this.managementPhases = phaseCallData?.datas;
            this.getPlanificationPhases();
            this.isBusy = false;
        }
    }

    async getRegroupingContractLines(projectId: number): Promise<void> {
        const CallData = await managementApi.getAllRegroupingContractLinesByProjectId(projectId);
        if (isCallValidAndNotCancelled<IRegroupingContractLine[]>(CallData)) {
            this.regroupingContractLineList = CallData?.datas ?? [];
        }
    }

    async saveManagementPhases(submitRegrouping : string = ''): Promise<void> {
        this.savePending = true;
        const callData = await managementApi.patchPhases(this.selectedProjectId!, this.managementPhases ?? []);
        if (isCallValidAndNotCancelled(callData)) {
            this.$bvToast.toast('Enregistrement effectué avec succès', {
                title: 'Gestion de projet',
                variant: 'success',
                solid: true
            });
            if (this.selectedProjectId && this.selectedProjectId !== 0 && !submitRegrouping) {
                await this.getManagementPhases(this.selectedProjectId);
            }
        }
        this.savePending = false;
    }

    private getBudget(management: IManagementPhase): number {
        if (management && management.contractLine) {
            return Number((management.contractLine.total - (management.contractLine.discount ?? 0) - 
            management.provisionalFeesValue - management.subContractsValue - ((management.contractLine.total / 100) * management.contractLine.marginPercentage)).toFixed(0));
        }
        return 0;
    }

    private progressAmount(management: IManagementPhase): number {
        if (management && management.contractLine) {
            if (management.contractLine.advancement === 0) return 0;
            if (management.contractLine.advancement === 100) {
                return Number((this.getBudget(management) - management.consumedRatesValue).toFixed(0));
            }
            return Number(
                (
                    this.getBudget(management) * (management.contractLine.advancement / 100) -
                    management.consumedRatesValue
                ).toFixed(0)
            );
        }
        return 0;
    }

    private setTarget(item: IManagementPhase): void {
        item.regroupingTotal = item.contractLine.total;
   
        this.managementPhases?.forEach(phase => {
            const contractLineInGroupe = this.regroupingContractLineList.find(regroupedContractLine => regroupedContractLine.attachedContractLineId === phase.contractLine.id);
            
            if (contractLineInGroupe) {
                phase.regroupingTotal = phase.contractLine.total;
            };
        });

        this.targetRegroupingContractLineId = item.contractLine.id;
        // (this.$refs['management-table'] as BTable).selectRow(this.managementPhases?.indexOf(item) ?? 0);
        this.setSelectedRowForGroupingLine(this.regroupingContractLineList);
    }

    private cancelRegrouping(item: IManagementPhase): void {
        this.targetRegroupingContractLineId = 0;
        this.fetchData(this.selectedProjectId!);
        this.setSelectedRowForGroupingLine(this.regroupingContractLineList);
    }

    private async submitRegrouping(item: IManagementPhase): Promise<void> {
        this.savePending = true;

        await this.saveManagementPhases('submitRegrouping');

        if (!this.selectedProjectId || this.selectedProjectId === 0) {
            return;
        }
        const CallData = await managementApi.patchRegroupingContractLines(
            this.selectedProjectId,
            this.regroupingContractLineList
        );

        if (isCallValidAndNotCancelled<boolean>(CallData)) {
            if (this.selectedProjectId && this.selectedProjectId !== 0) {
                this.fetchData(this.selectedProjectId);
            }
            this.savePending = false;
        }
    }

    private addRegrouping(item: IManagementPhase): void {
        const exist = this.regroupingContractLineList?.find(
            (x) =>
                x.attachedContractLineId === item.contractLine.id &&
                x.targetContractLineId === this.targetRegroupingContractLineId
        );
        if (exist) {
            const index = this.regroupingContractLineList?.indexOf(exist);
            if (index !== undefined && index > -1) {
                this.regroupingContractLineList?.splice(index, 1);
            }
        } else {
            this.regroupingContractLineList?.push({
                projectId: this.selectedProjectId,
                targetContractLineId: this.targetRegroupingContractLineId,
                attachedContractLineId: item.contractLine.id
            } as IRegroupingContractLine);
            this.setAttachedContractLinesPercentageById(this.targetRegroupingContractLineId);
        }
    }

    private getAttachedNumber(item: IManagementPhase): number {
        return (
            this.regroupingContractLineList?.filter((x) => x.targetContractLineId === item.contractLine.id)?.length ?? 0
        );
    }

    private isAlreadyRegrouping(item: IManagementPhase): boolean {
        return this.getAttachedNumber(item) > 0;
    }

    sortDown(item: IManagementPhase): void {
        let index = this.managementPhases?.indexOf(item) ?? -1;
        let regroupingIndexsToMove: number[] = [];
        if (index !== undefined && index > -1 && this.managementPhases) {
            do {
                regroupingIndexsToMove = [];
                this.managementPhases.forEach((element, indexArray) => {
                    const isRegroupingElement =
                        this.regroupingContractLineList.find(
                            (y) =>
                                y.targetContractLineId === item.contractLine.id &&
                                y.attachedContractLineId === element.contractLine.id
                        ) !== undefined;
                    if (isRegroupingElement) {
                        regroupingIndexsToMove.push(indexArray);
                    }
                });
                regroupingIndexsToMove = regroupingIndexsToMove.reverse();
                regroupingIndexsToMove.forEach((element) => {
                    this.managementPhases?.splice(element + 1, 0, this.managementPhases?.splice(element, 1)[0]);
                });
                this.managementPhases?.splice(index + 1, 0, this.managementPhases?.splice(index, 1)[0]);
                index = this.managementPhases?.indexOf(item) ?? -1;
            } while (
                this.managementPhases &&
                index < this.managementPhases.length - 1 &&
                this.regroupingContractLineList.find(
                    (y) =>
                        this.managementPhases &&
                        y.attachedContractLineId === this.managementPhases[index + 1].contractLine.id &&
                        y.targetContractLineId !== item.contractLine.id
                ) !== undefined
            );
        }
    }

    sortUp(item: IManagementPhase): void {
        let index = this.managementPhases?.indexOf(item) ?? -1;
        let regroupingIndexsToMove: number[] = [];
        if (index !== undefined && index > -1 && this.managementPhases) {
            do {
                regroupingIndexsToMove = [];
                this.managementPhases?.splice(index - 1, 0, this.managementPhases?.splice(index, 1)[0]);
                this.managementPhases.forEach((element, indexArray) => {
                    const isRegroupingElement =
                        this.regroupingContractLineList.find(
                            (y) =>
                                y.targetContractLineId === item.contractLine.id &&
                                y.attachedContractLineId === element.contractLine.id
                        ) !== undefined;
                    if (isRegroupingElement) {
                        regroupingIndexsToMove.push(indexArray);
                    }
                });
                regroupingIndexsToMove.forEach((element) => {
                    this.managementPhases?.splice(element - 1, 0, this.managementPhases?.splice(element, 1)[0]);
                });
                index = this.managementPhases?.indexOf(item) ?? -1;
            } while (
                index > 0 &&
                this.regroupingContractLineList.find(
                    (y) =>
                        this.managementPhases &&
                        y.targetContractLineId === this.managementPhases[index - 1].contractLine.id
                ) !== undefined
            );
        }
    }

    private async mounted(): Promise<void> {
        this.initComponent();
        vxm.user.checkIsAssociate();
        vxm.user.checkIsAdmin();
        vxm.user.checkIsDaf();
        vxm.user.checkIsStudioManager();
        vxm.user.checkIsRH();
        vxm.user.checkIsCP();
    }

    private initComponent() {
        vxm.app.changeTitleMain('Gestion de projet');
        if (this.selectedProjectId && this.selectedProjectId !== 0) {
            this.fetchData(this.selectedProjectId!);
        }
    }

    private async fetchData(projectId: number): Promise<void> {
        this.targetRegroupingContractLineId = 0;
        await this.getRegroupingContractLines(projectId);
        await this.getManagementPhases(projectId);
    }

    async exportListManagement(): Promise<void> {
        await this.generateReport(
            managementApi.exportManagementData({ data: this.selectedProjectId } as any),
            `Gestion_de_projet_${this.selectedProject?.designation}_${this.formatDate(new Date())}.xlsx`
        );
        this.fetchData(this.selectedProjectId!);
    }

    private formatDate(date: Date): string {
        return format(new Date(String(date)), 'yyyy-MM-dd', { locale: frenchLocale }) ?? '';
    }

    private async generateReport(request: Promise<ICancellableResult<string>>, reportName: string): Promise<void> {
        this.promiseExport = true;
        const response = await request;
        if (response && response.datas) {
            const blob = new Blob([response.datas], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;'
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = reportName;
            link.click();
            this.promiseExport = false;
        }
        // We wait for the file to download before removing loading state
        setTimeout(() => {
            this.promiseExport = false;
        }, 1000);
    }

    handleChangeAdvancement(): void {
        this.advancementKey++;
    }

    rowClass(item, type): any {
        if (!item || type !== 'row') {
            return;
        } 
        if (!item || !item.contractLine?.statusId) return 'status-default'; // Évite les erreurs si item est null/undefined
      
         switch (item.contractLine?.status.code) {
           case 'ACT': return 'status-actif';  
           case 'PRV': return 'status-provisional'; 
           case 'BLC': return 'status-blocked '; 
           default: return 'status-default'; 
     }
}
}
